import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const systemVariablesApi = createApi({
  reducerPath: "systemVariables",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: [
    "SystemFoods",
    "SystemDiagnosisCategories",
    "SystemDiagnoses",
    "SystemLabTests",
    "SystemMedicines",
    "SystemImages",
    "SystemComplaints",
    "SystemAllergies",
  ],
  endpoints: (builder) => ({
    listFoods: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/foods/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemFoods"],
    }),
    createFood: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/foods/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemFoods"],
    }),
    bulkUpsertFoods: builder.mutation({
      query: ({ token, items }) => {
        return {
          url: "/api/admin/system-variables/foods/bulk",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { items },
        };
      },
      invalidatesTags: ["SystemFoods"],
    }),
    updateFood: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/foods/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemFoods"],
    }),
    setFoodStatus: builder.mutation({
      query: ({ token, foodId, isActive }) => {
        return {
          url: "/api/admin/system-variables/foods/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { foodId, isActive },
        };
      },
      invalidatesTags: ["SystemFoods"],
    }),
    deleteFood: builder.mutation({
      query: ({ token, foodId }) => {
        return {
          url: "/api/admin/system-variables/foods/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { foodId },
        };
      },
      invalidatesTags: ["SystemFoods"],
    }),

    // Diagnosis Categories
    listDiagnosisCategories: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/diagnosis/categories/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemDiagnosisCategories"],
    }),
    createDiagnosisCategory: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/categories/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemDiagnosisCategories", "SystemDiagnoses"],
    }),
    updateDiagnosisCategory: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/categories/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemDiagnosisCategories", "SystemDiagnoses"],
    }),
    setDiagnosisCategoryStatus: builder.mutation({
      query: ({ token, categoryId, isActive }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/categories/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { categoryId, isActive },
        };
      },
      invalidatesTags: ["SystemDiagnosisCategories", "SystemDiagnoses"],
    }),
    deleteDiagnosisCategory: builder.mutation({
      query: ({ token, categoryId }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/categories/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { categoryId },
        };
      },
      invalidatesTags: ["SystemDiagnosisCategories"],
    }),

    // Diagnoses
    listDiagnoses: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/diagnosis/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemDiagnoses"],
    }),
    createDiagnosis: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemDiagnoses"],
    }),
    updateDiagnosis: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemDiagnoses"],
    }),
    setDiagnosisStatus: builder.mutation({
      query: ({ token, diagnosisId, isActive }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { diagnosisId, isActive },
        };
      },
      invalidatesTags: ["SystemDiagnoses"],
    }),
    deleteDiagnosis: builder.mutation({
      query: ({ token, diagnosisId }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { diagnosisId },
        };
      },
      invalidatesTags: ["SystemDiagnoses"],
    }),

    // Lab Tests (Tahliller)
    listLabTests: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/labs/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemLabTests"],
    }),
    createLabTest: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/labs/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemLabTests"],
    }),
    updateLabTest: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/labs/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemLabTests"],
    }),
    setLabTestStatus: builder.mutation({
      query: ({ token, labTestId, isActive }) => {
        return {
          url: "/api/admin/system-variables/labs/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { labTestId, isActive },
        };
      },
      invalidatesTags: ["SystemLabTests"],
    }),
    deleteLabTest: builder.mutation({
      query: ({ token, labTestId }) => {
        return {
          url: "/api/admin/system-variables/labs/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { labTestId },
        };
      },
      invalidatesTags: ["SystemLabTests"],
    }),
    bulkUpsertLabTests: builder.mutation({
      query: ({ token, items }) => {
        return {
          url: "/api/admin/system-variables/labs/bulk",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { items },
        };
      },
      invalidatesTags: ["SystemLabTests"],
    }),

    bulkUpsertDiagnoses: builder.mutation({
      query: ({ token, items }) => {
        return {
          url: "/api/admin/system-variables/diagnosis/bulk",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { items },
        };
      },
      invalidatesTags: ["SystemDiagnoses", "SystemDiagnosisCategories"],
    }),

    // Medicines (İlaçlar)
    listMedicineTemplates: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/medicines/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemMedicines"],
    }),
    createMedicineTemplate: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/medicines/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemMedicines"],
    }),
    updateMedicineTemplate: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/medicines/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemMedicines"],
    }),
    setMedicineTemplateStatus: builder.mutation({
      query: ({ token, medicineId, isActive }) => {
        return {
          url: "/api/admin/system-variables/medicines/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { medicineId, isActive },
        };
      },
      invalidatesTags: ["SystemMedicines"],
    }),
    deleteMedicineTemplate: builder.mutation({
      query: ({ token, medicineId }) => {
        return {
          url: "/api/admin/system-variables/medicines/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { medicineId },
        };
      },
      invalidatesTags: ["SystemMedicines"],
    }),
    bulkUpsertMedicineTemplates: builder.mutation({
      query: ({ token, items }) => {
        return {
          url: "/api/admin/system-variables/medicines/bulk",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { items },
        };
      },
      invalidatesTags: ["SystemMedicines"],
    }),
    // Images (Görüntüler)
    listImages: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/images/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemImages"],
    }),
    createImage: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/images/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemImages"],
    }),
    bulkUpsertImages: builder.mutation({
      query: ({ token, items }) => {
        return {
          url: "/api/admin/system-variables/images/bulk",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { items },
        };
      },
      invalidatesTags: ["SystemImages"],
    }),
    updateImage: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/images/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemImages"],
    }),
    setImageStatus: builder.mutation({
      query: ({ token, imageId, isActive }) => {
        return {
          url: "/api/admin/system-variables/images/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { imageId, isActive },
        };
      },
      invalidatesTags: ["SystemImages"],
    }),
    deleteImage: builder.mutation({
      query: ({ token, imageId }) => {
        return {
          url: "/api/admin/system-variables/images/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { imageId },
        };
      },
      invalidatesTags: ["SystemImages"],
    }),

    listComplaints: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/complaints/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemComplaints"],
    }),
    createComplaint: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/complaints/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemComplaints"],
    }),
    updateComplaint: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/complaints/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemComplaints"],
    }),
    setComplaintStatus: builder.mutation({
      query: ({ token, complaintId, isActive }) => {
        return {
          url: "/api/admin/system-variables/complaints/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { complaintId, isActive },
        };
      },
      invalidatesTags: ["SystemComplaints"],
    }),
    deleteComplaint: builder.mutation({
      query: ({ token, complaintId }) => {
        return {
          url: "/api/admin/system-variables/complaints/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { complaintId },
        };
      },
      invalidatesTags: ["SystemComplaints"],
    }),
    bulkUpsertComplaints: builder.mutation({
      query: ({ token, items }) => {
        return {
          url: "/api/admin/system-variables/complaints/bulk",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { items },
        };
      },
      invalidatesTags: ["SystemComplaints"],
    }),

    listAllergies: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/clinic/system-variables/allergies/list",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemAllergies"],
    }),
    createAllergy: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/allergies/create",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemAllergies"],
    }),
    updateAllergy: builder.mutation({
      query: ({ token, ...payload }) => {
        return {
          url: "/api/admin/system-variables/allergies/update",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { ...payload },
        };
      },
      invalidatesTags: ["SystemAllergies"],
    }),
    setAllergyStatus: builder.mutation({
      query: ({ token, allergyId, isActive }) => {
        return {
          url: "/api/admin/system-variables/allergies/status",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { allergyId, isActive },
        };
      },
      invalidatesTags: ["SystemAllergies"],
    }),
    deleteAllergy: builder.mutation({
      query: ({ token, allergyId }) => {
        return {
          url: "/api/admin/system-variables/allergies/delete",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { allergyId },
        };
      },
      invalidatesTags: ["SystemAllergies"],
    }),
    bulkUpsertAllergies: builder.mutation({
      query: ({ token, items }) => {
        return {
          url: "/api/admin/system-variables/allergies/bulk",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { items },
        };
      },
      invalidatesTags: ["SystemAllergies"],
    }),
  }),
});

export const {
  useListFoodsMutation,
  useCreateFoodMutation,
  useBulkUpsertFoodsMutation,
  useUpdateFoodMutation,
  useSetFoodStatusMutation,
  useDeleteFoodMutation,
  useListDiagnosisCategoriesMutation,
  useCreateDiagnosisCategoryMutation,
  useUpdateDiagnosisCategoryMutation,
  useSetDiagnosisCategoryStatusMutation,
  useDeleteDiagnosisCategoryMutation,
  useListDiagnosesMutation,
  useCreateDiagnosisMutation,
  useUpdateDiagnosisMutation,
  useSetDiagnosisStatusMutation,
  useDeleteDiagnosisMutation,
  useListLabTestsMutation,
  useCreateLabTestMutation,
  useUpdateLabTestMutation,
  useSetLabTestStatusMutation,
  useDeleteLabTestMutation,
  useBulkUpsertLabTestsMutation,
  useBulkUpsertDiagnosesMutation,
  useListMedicineTemplatesMutation,
  useCreateMedicineTemplateMutation,
  useUpdateMedicineTemplateMutation,
  useSetMedicineTemplateStatusMutation,
  useDeleteMedicineTemplateMutation,
  useBulkUpsertMedicineTemplatesMutation,
  useListImagesMutation,
  useCreateImageMutation,
  useBulkUpsertImagesMutation,
  useUpdateImageMutation,
  useSetImageStatusMutation,
  useDeleteImageMutation,
  useListComplaintsMutation,
  useCreateComplaintMutation,
  useUpdateComplaintMutation,
  useSetComplaintStatusMutation,
  useDeleteComplaintMutation,
  useBulkUpsertComplaintsMutation,
  useListAllergiesMutation,
  useCreateAllergyMutation,
  useUpdateAllergyMutation,
  useSetAllergyStatusMutation,
  useDeleteAllergyMutation,
  useBulkUpsertAllergiesMutation,
} = systemVariablesApi;
